import ExcelJS from "exceljs";
import dayjs from "dayjs";

const BRAND_BLUE = "FF081E56";
const PALE_BLUE = "FFF4F8FD";
const WHITE = "FFFFFFFF";
const BORDER = "FFD0D5DD";

const toDate = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [year, month, day] = String(value).split("-").map(Number);
    // Keep date-only fields stable when ExcelJS serialises them in another timezone.
    // Local midnight during BST can otherwise be written as the previous UTC date.
    return new Date(Date.UTC(year, month - 1, day, 12));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const styleSheet = (sheet, widths, currencyColumns = [], dateColumns = []) => {
  sheet.views = [{ state: "frozen", ySplit: 1, showGridLines: false }];
  sheet.autoFilter = { from: "A1", to: sheet.getRow(1).getCell(widths.length).address };
  sheet.properties.defaultRowHeight = 20;
  sheet.columns.forEach((column, index) => { column.width = widths[index]; });

  const header = sheet.getRow(1);
  header.height = 28;
  header.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: BRAND_BLUE } };
    cell.font = { bold: true, color: { argb: WHITE } };
    cell.alignment = { vertical: "middle", horizontal: "left", wrapText: true };
  });

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber > 1 && rowNumber % 2 === 0) {
      row.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: PALE_BLUE } };
      });
    }
    row.eachCell((cell) => {
      cell.border = {
        top: { style: "thin", color: { argb: BORDER } },
        left: { style: "thin", color: { argb: BORDER } },
        bottom: { style: "thin", color: { argb: BORDER } },
        right: { style: "thin", color: { argb: BORDER } },
      };
      cell.alignment = { vertical: "top", wrapText: true };
    });
  });

  currencyColumns.forEach((columnNumber) => {
    sheet.getColumn(columnNumber).numFmt = '£#,##0.00';
  });
  dateColumns.forEach((columnNumber) => {
    sheet.getColumn(columnNumber).numFmt = "dd/mm/yyyy";
  });
};

const addWebsiteDesignSheet = (workbook, websites) => {
  const sheet = workbook.addWorksheet("Website Design", { views: [{ showGridLines: false }] });
  sheet.addRow(["Adviser", "URL", "Live Date", "Amount To Charge", "Charge Across", "Complete (Can Charge)", "Notes"]);
  websites.forEach((website) => {
    (website.domains || []).forEach((domain) => {
    const row = sheet.addRow([
      website.adviserName || "",
      domain.domainName || "",
      toDate(domain.design?.liveDate),
      toNumber(domain.design?.amountToCharge),
      toNumber(domain.design?.chargeAcross),
      "",
      domain.notes || website.notes || "",
    ]);
    row.getCell(6).value = {
      formula: `IF(AND(C${row.number}<>"",D${row.number}<>""),"Yes","No")`,
      result: domain.design?.liveDate && domain.design?.amountToCharge ? "Yes" : "No",
    };
    });
  });
  styleSheet(sheet, [24, 34, 15, 20, 18, 23, 42], [4], [3]);
};

const addPipelineSheet = (workbook, websites) => {
  const sheet = workbook.addWorksheet("Website Pipeline", { views: [{ showGridLines: false }] });
  sheet.addRow([
    "Date Entry Created", "Date Adviser Informed", "Adviser Name", "Adviser Email",
    "AR/TS Name", "Estimated Turnaround Date", "Status", "Assigned To",
    "Website Form", "Notes", "Created At", "Last Updated",
  ]);
  websites.forEach((website) => {
    const row = sheet.addRow([
      toDate(website.dateStarted),
      toDate(website.dateAdviserInformed),
      website.adviserName || "",
      website.adviserEmail || "",
      website.arTsName || "",
      toDate(website.estimatedTurnaroundDate),
      website.status || "",
      website.assignedTo || "",
      website.websiteForm?.name || "",
      website.notes || "",
      toDate(website.createdAt || website.createdAtMs),
      toDate(website.updatedAt),
    ]);
    if (website.websiteForm?.url) {
      row.getCell(9).value = {
        text: website.websiteForm.name || "Open uploaded form",
        hyperlink: website.websiteForm.url,
      };
      row.getCell(9).font = { color: { argb: "FF0563C1" }, underline: true };
    }
  });
  styleSheet(
    sheet,
    [20, 22, 24, 34, 24, 25, 20, 24, 32, 45, 22, 22],
    [],
    [1, 2, 6, 11, 12],
  );
  sheet.getColumn(11).numFmt = "dd/mm/yyyy hh:mm";
  sheet.getColumn(12).numFmt = "dd/mm/yyyy hh:mm";
};

const addDomainSheet = (workbook, websites) => {
  const sheet = workbook.addWorksheet("Domain Registration & Transfer", { views: [{ showGridLines: false }] });
  sheet.addRow([
    "Adviser", "Domain", "Registration or Transfer", "No of Years", "Registrar",
    "Date Paid", "Amount Paid", "Paid By", "Markup (%)", "Amount To Charge", "Notes",
  ]);
  websites.forEach((website) => {
    (website.domains || []).forEach((domain) => {
    const amountPaid = toNumber(domain.amountPaid);
    const markup = toNumber(domain.markup);
    const row = sheet.addRow([
      website.adviserName || "", domain.domainName || "", domain.registrationType || "",
      toNumber(domain.numberOfYears), domain.registrar || "", toDate(domain.datePaid),
      amountPaid, domain.paidBy || "", markup, "", domain.notes || "",
    ]);
    const result = amountPaid === null ? null : amountPaid * (1 + (markup || 0) / 100);
    row.getCell(10).value = {
      formula: `IF(G${row.number}="","",G${row.number}*(1+I${row.number}/100))`,
      result: result ?? "",
    };
    });
  });
  styleSheet(sheet, [24, 34, 25, 14, 20, 15, 16, 20, 14, 20, 42], [7, 10], [6]);
  sheet.getColumn(9).numFmt = "0.00";
};

const addHostingSheet = (workbook, websites) => {
  const sheet = workbook.addWorksheet("Website Hosting", { views: [{ showGridLines: false }] });
  sheet.addRow(["Adviser", "URL", "Monthly Charge", "Live Date", "Notes"]);
  websites.forEach((website) => {
    (website.domains || []).forEach((domain) => {
    sheet.addRow([
      website.adviserName || "", domain.domainName || "",
      toNumber(domain.hosting?.monthlyCharge), toDate(domain.design?.liveDate),
      domain.notes || website.notes || "",
    ]);
    });
  });
  styleSheet(sheet, [24, 34, 20, 15, 42], [3], [4]);
};

const addPrintingChargesSheet = (workbook, printingCharges) => {
  const sheet = workbook.addWorksheet("Printing Charges", { views: [{ showGridLines: false }] });
  sheet.addRow([
    "Adviser", "Adviser Email", "Order Items", "Supplier", "Order Date", "Order Number",
    "Order Description", "Total Paid", "Markup (%)", "Amount To Charge",
    "Charge Across (Months)", "Invoice", "Created At", "Last Updated",
  ]);

  printingCharges.forEach((charge) => {
    const row = sheet.addRow([
      charge.adviserName || "",
      charge.adviserEmail || "",
      Array.isArray(charge.orderItems) ? charge.orderItems.join(", ") : "",
      charge.supplier || "",
      toDate(charge.orderDate),
      charge.orderNumber || "",
      charge.orderDescription || "",
      toNumber(charge.totalPaid),
      toNumber(charge.markup),
      toNumber(charge.amountToCharge),
      toNumber(charge.chargeAcross),
      charge.invoice?.name || "",
      toDate(charge.createdAt || charge.createdAtMs),
      toDate(charge.updatedAt || charge.updatedAtMs),
    ]);

    if (charge.invoice?.url) {
      row.getCell(12).value = {
        text: charge.invoice.name || "Open invoice",
        hyperlink: charge.invoice.url,
      };
      row.getCell(12).font = { color: { argb: "FF0563C1" }, underline: true };
    }
  });

  styleSheet(
    sheet,
    [24, 34, 42, 22, 16, 20, 42, 16, 14, 20, 22, 30, 22, 22],
    [8, 10],
    [5, 13, 14],
  );
  sheet.getColumn(9).numFmt = "0.00";
  sheet.getColumn(13).numFmt = "dd/mm/yyyy hh:mm";
  sheet.getColumn(14).numFmt = "dd/mm/yyyy hh:mm";
};

const addEmailLicencesSheet = (workbook, licences) => {
  const sheet = workbook.addWorksheet("Email Licences", { views: [{ showGridLines: false }] });
  sheet.addRow(["Adviser", "Adviser Email", "Licence Description", "Number of Licences", "Date Requested", "Charge From", "Licence Email Address", "Domain Linked", "Monthly Charge", "Notes", "Invoice", "Created At", "Last Updated"]);
  licences.forEach((licence) => {
    const row = sheet.addRow([licence.adviserName || "", licence.adviserEmail || "", licence.licenceType || "", toNumber(licence.numberOfLicences), toDate(licence.orderDate), toDate(licence.chargeFromDate || licence.orderDate), licence.emailAddress || "", licence.domainLinked || "", toNumber(licence.monthlyAmount), licence.notes || "", licence.invoice?.name || "", toDate(licence.createdAt || licence.createdAtMs), toDate(licence.updatedAt || licence.updatedAtMs)]);
    if (licence.invoice?.url) {
      row.getCell(11).value = { text: licence.invoice.name || "Open invoice", hyperlink: licence.invoice.url };
      row.getCell(11).font = { color: { argb: "FF0563C1" }, underline: true };
    }
  });
  styleSheet(sheet, [24, 34, 34, 20, 16, 16, 34, 30, 18, 42, 30, 22, 22], [9], [5, 6, 12, 13]);
  sheet.getColumn(12).numFmt = "dd/mm/yyyy hh:mm";
  sheet.getColumn(13).numFmt = "dd/mm/yyyy hh:mm";
};

export const buildWebsiteChargesWorkbook = (websites = [], printingCharges = [], emailLicences = []) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Rosemount Staff Portal";
  workbook.created = new Date();
  workbook.modified = new Date();

  addPrintingChargesSheet(workbook, printingCharges);
  addEmailLicencesSheet(workbook, emailLicences);
  addPipelineSheet(workbook, websites);
  addWebsiteDesignSheet(workbook, websites);
  addDomainSheet(workbook, websites);
  addHostingSheet(workbook, websites);

  return workbook;
};

export const downloadWebsiteChargesExcel = async (websites = [], printingCharges = [], emailLicences = []) => {
  const workbook = buildWebsiteChargesWorkbook(websites, printingCharges, emailLicences);
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Marketing-Charges-${dayjs().format("YYYY-MM-DD")}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
